import type { UseFormReturnType } from "@mantine/form"
import { useCallback, useRef, type RefObject } from "react"

type UseOnAutocompleteValueChangedParams = {
  form: UseFormReturnType<Record<string, unknown>>
  organizationNameFieldName: string
  linkedRepositoryCompanyNameRef: RefObject<string | null>
  abortDirectoryPrefillAndResetOrganizationFields: () => void
  onBusinessPartnerRepositorySelectionCleared?: () => void
  onDuplicateCandidateForCreateNew?: (
    duplicateCandidateId: string | null
  ) => void
}

export const useOnAutocompleteValueChanged = ({
  form,
  organizationNameFieldName,
  linkedRepositoryCompanyNameRef,
  abortDirectoryPrefillAndResetOrganizationFields,
  onBusinessPartnerRepositorySelectionCleared,
  onDuplicateCandidateForCreateNew,
}: UseOnAutocompleteValueChangedParams) => {
  /** Name set by create-anyway; duplicate candidate metadata applies only while this matches. */
  const createAnywayChosenNameRef = useRef<string | null>(null)

  const clearDuplicateCandidateIfCreateAnywayNameDiverged = useCallback(
    (next: string): void => {
      const createAnywayChosenName = createAnywayChosenNameRef.current
      if (createAnywayChosenName === null || next === createAnywayChosenName) {
        return
      }
      createAnywayChosenNameRef.current = null
      onDuplicateCandidateForCreateNew?.(null)
    },
    [onDuplicateCandidateForCreateNew]
  )

  const onAutocompleteValueChange = useCallback(
    (next: string) => {
      const linkedRepositoryCompanyName = linkedRepositoryCompanyNameRef.current
      const hasDivergedFromLinkedDirectoryName =
        linkedRepositoryCompanyName !== null &&
        next !== linkedRepositoryCompanyName

      if (hasDivergedFromLinkedDirectoryName) {
        linkedRepositoryCompanyNameRef.current = null
        abortDirectoryPrefillAndResetOrganizationFields()
        onBusinessPartnerRepositorySelectionCleared?.()
      }

      clearDuplicateCandidateIfCreateAnywayNameDiverged(next)

      form.setFieldValue(organizationNameFieldName, next)
    },
    [
      abortDirectoryPrefillAndResetOrganizationFields,
      clearDuplicateCandidateIfCreateAnywayNameDiverged,
      form,
      linkedRepositoryCompanyNameRef,
      onBusinessPartnerRepositorySelectionCleared,
      organizationNameFieldName,
    ]
  )

  const onAutocompleteClear = useCallback(() => {
    const hadDirectoryNameLink = linkedRepositoryCompanyNameRef.current !== null
    const hadCreateAnywayDuplicateCandidate =
      createAnywayChosenNameRef.current !== null
    linkedRepositoryCompanyNameRef.current = null
    createAnywayChosenNameRef.current = null

    if (hadCreateAnywayDuplicateCandidate) {
      onDuplicateCandidateForCreateNew?.(null)
    }

    if (!hadDirectoryNameLink) {
      return
    }

    abortDirectoryPrefillAndResetOrganizationFields()
    onBusinessPartnerRepositorySelectionCleared?.()
  }, [
    abortDirectoryPrefillAndResetOrganizationFields,
    linkedRepositoryCompanyNameRef,
    onBusinessPartnerRepositorySelectionCleared,
    onDuplicateCandidateForCreateNew,
  ])

  /**
   * User chose manual create with `chosenName` (directory opt-out): clear linked directory
   * state, reset organization fields filled from directory prefill, and sync the name field.
   */
  const onCreateNewWithoutDirectorySelection = useCallback(
    (chosenName: string, duplicateCandidateId: string | null): void => {
      linkedRepositoryCompanyNameRef.current = null
      createAnywayChosenNameRef.current = chosenName
      abortDirectoryPrefillAndResetOrganizationFields()
      onBusinessPartnerRepositorySelectionCleared?.()
      onDuplicateCandidateForCreateNew?.(duplicateCandidateId)
      form.setFieldValue(organizationNameFieldName, chosenName)
    },
    [
      abortDirectoryPrefillAndResetOrganizationFields,
      form,
      linkedRepositoryCompanyNameRef,
      onBusinessPartnerRepositorySelectionCleared,
      onDuplicateCandidateForCreateNew,
      organizationNameFieldName,
    ]
  )

  return {
    onAutocompleteValueChange,
    onAutocompleteClear,
    onCreateNewWithoutDirectorySelection,
  }
}
